import {
  asClause,
  fillFrame,
  joinSentences,
  properWords,
} from "@/lib/tool-kit/essay/sentence";

/**
 * Counterargument-and-rebuttal paragraphs.
 *
 * Three strategies, three wordings each. Every wording is five sentence frames in a
 * fixed order — the opposing view, its best reason, a concession (or, for "turn the
 * tables", the pivot), the rebuttal and the return to the writer's claim — so the
 * widget can label each sentence with the job it does. The frames are the English
 * defaults; the widget passes its merged strings in, which is what lets a translated
 * page supply its own. The page locale decides whether a field's first letter is
 * lowered when it moves mid-sentence and how the sentences are joined.
 */

export type CounterStrategy = "concede" | "partial" | "turn";

export const COUNTER_STRATEGIES: readonly CounterStrategy[] = [
  "concede",
  "partial",
  "turn",
];

export type CounterPart =
  | "opposing"
  | "reason"
  | "concession"
  | "pivot"
  | "rebuttal"
  | "claim";

/** The part each frame position fills, per strategy. */
export const COUNTER_PARTS: Record<CounterStrategy, readonly CounterPart[]> = {
  concede: ["opposing", "reason", "concession", "rebuttal", "claim"],
  partial: ["opposing", "reason", "concession", "rebuttal", "claim"],
  turn: ["opposing", "reason", "pivot", "rebuttal", "claim"],
};

/** How many wordings each strategy has; frame keys are `<strategy>-1` … `-3`. */
export const COUNTER_VERSIONS = 3;

/** English frames. `{claim}`, `{opposing}`, `{reason}` and `{response}` are the fields. */
export const COUNTER_FRAMES = {
  "concede-1": [
    "Some readers will object that {opposing}.",
    "Their strongest point is that {reason}.",
    "That objection is understandable, and it deserves a direct answer.",
    "However, {response}.",
    "The objection therefore does not hold, and {claim}.",
  ],
  "concede-2": [
    "Opponents of this position argue that {opposing}.",
    "They point out that {reason}.",
    "On the surface, this seems persuasive.",
    "A closer look, however, tells a different story. {response}.",
    "For this reason, {claim}.",
  ],
  "concede-3": [
    "It could be argued that {opposing}.",
    "After all, {reason}.",
    "This view has some intuitive appeal.",
    "Yet it does not survive scrutiny, because {response}.",
    "It follows that {claim}.",
  ],
  "partial-1": [
    "Those who disagree argue that {opposing}.",
    "They are right that {reason}.",
    "That point is fair, and any serious proposal has to account for it.",
    "Even so, the problem can be solved without abandoning the larger goal. {response}.",
    "With that concern addressed, {claim}.",
  ],
  "partial-2": [
    "A reasonable objection is that {opposing}.",
    "It is true that {reason}.",
    "This deserves to be taken seriously.",
    "However, it is a reason to plan the change carefully, not a reason to reject it. {response}.",
    "On balance, then, {claim}.",
  ],
  "partial-3": [
    "Critics may counter that {opposing}.",
    "Admittedly, {reason}.",
    "This point has merit.",
    "Still, it does not outweigh the case for change, because {response}.",
    "The stronger conclusion is that {claim}.",
  ],
  "turn-1": [
    "Opponents argue that {opposing}.",
    "They point to the fact that {reason}.",
    "Yet this evidence, read carefully, supports the opposite conclusion.",
    "{response}.",
    "Far from undermining the case, their own point suggests that {claim}.",
  ],
  "turn-2": [
    "Some will say that {opposing}.",
    "Their reasoning is that {reason}.",
    "But that same fact cuts the other way.",
    "{response}.",
    "In other words, the opponents' best point is actually a reason why {claim}.",
  ],
  "turn-3": [
    "The usual objection is that {opposing}.",
    "The reason given is that {reason}.",
    "That premise is sound, but it leads somewhere its defenders do not intend.",
    "{response}.",
    "Taken seriously, the objection strengthens the case that {claim}.",
  ],
} as const;

/** Sentence frames to adapt by hand: conceding a point. */
export const COUNTER_CONCESSION_FRAMES = [
  "Admittedly, ___.",
  "It is true that ___.",
  "Critics are right to point out that ___.",
  "To be fair, ___.",
  "Granted, ___.",
  "There is some truth to the claim that ___.",
  "This objection has force when ___.",
  "No one would deny that ___.",
  "Those who favor ___ make a fair point about ___.",
  "It would be a mistake to dismiss the concern that ___.",
] as const;

/** Sentence frames to adapt by hand: answering the objection. */
export const COUNTER_REBUTTAL_FRAMES = [
  "However, this view overlooks ___.",
  "Even so, ___.",
  "This argument assumes that ___, but ___.",
  "The evidence points the other way: ___.",
  "Even if ___, that alone does not show that ___.",
  "This concern is real, but it can be met by ___.",
  "On closer inspection, ___.",
  "What this objection misses is ___.",
  "The weakness in this reasoning is that ___.",
  "Ironically, the same evidence suggests that ___.",
] as const;

/** Sentence frames to adapt by hand: returning to the writer's claim. */
export const COUNTER_RETURN_FRAMES = [
  "For these reasons, the case for ___ still stands.",
  "The objection, then, does not undermine the claim that ___.",
  "On balance, ___.",
  "This is precisely why ___.",
  "Once this is taken into account, it becomes clear that ___.",
  "The objection refines the argument rather than defeating it: ___.",
] as const;

export interface CounterInput {
  claim: string;
  opposing: string;
  reason: string;
  response: string;
}

export type CounterField = keyof CounterInput;

export interface FramedSentence<P extends string = string> {
  part: P;
  text: string;
}

/**
 * The fields a strategy cannot do without. The opposing side's reason is optional —
 * except when turning the tables, where it is the evidence being turned around.
 */
export function missingCounterFields(
  input: CounterInput,
  strategy: CounterStrategy
): CounterField[] {
  const required: CounterField[] =
    strategy === "turn"
      ? ["claim", "opposing", "reason", "response"]
      : ["claim", "opposing", "response"];
  return required.filter((field) => input[field].trim() === "");
}

/** Every wording of the chosen strategy, each as labelled sentences. */
export function buildCounterParagraphs(
  input: CounterInput,
  strategy: CounterStrategy,
  frames: Readonly<Record<string, readonly string[]>> = COUNTER_FRAMES,
  locale = "en"
): FramedSentence<CounterPart>[][] {
  const proper = properWords(Object.values(input));
  const values: Record<string, string | null> = {
    claim: asClause(input.claim, proper, locale),
    opposing: asClause(input.opposing, proper, locale),
    reason:
      input.reason.trim() === ""
        ? null
        : asClause(input.reason, proper, locale),
    response: asClause(input.response, proper, locale),
  };
  const parts = COUNTER_PARTS[strategy];
  const paragraphs: FramedSentence<CounterPart>[][] = [];
  for (let version = 1; version <= COUNTER_VERSIONS; version++) {
    const templates = frames[`${strategy}-${version}`] ?? [];
    const sentences: FramedSentence<CounterPart>[] = [];
    for (const [index, template] of templates.entries()) {
      const part = parts[index];
      const text = part ? fillFrame(template, values, locale) : null;
      if (part && text) {
        sentences.push({ part, text });
      }
    }
    paragraphs.push(sentences);
  }
  return paragraphs;
}

/** A labelled paragraph as the plain text a reader pastes. */
export function paragraphText(
  sentences: readonly FramedSentence[],
  locale = "en"
): string {
  return joinSentences(
    sentences.map((sentence) => sentence.text),
    locale
  );
}
